import React, { HTMLInputTypeAttribute } from 'react'
import styled from 'styled-components'
import { ErrorMessage } from '@hookform/error-message'
import HelperTooltip from './HelperTooltip'

interface StyledInputContainerProps {
  verticallySpaced: boolean
  verticallySpace: number
  type?: HTMLInputTypeAttribute
}

const StyledInputContainer = styled.div<StyledInputContainerProps>(
  ({ theme, type, verticallySpace, verticallySpaced }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    margin: `0 0 ${verticallySpaced ? theme.space(verticallySpace) : '0'} 0`,
  })
)

export const StyledLabel = styled.label<{ $type?: HTMLInputTypeAttribute }>(
  ({ $type }) => ({
    fontSize: '1.4rem',
    fontWeight: 'bold',
    display: 'flex',
  })
)

const StyledHelpLabel = styled.label(({ theme }) => ({
  fontSize: '1.4rem',
  color: theme.colors.base_40,
  fontWight: '400',
  marginTop: theme.space(1),
}))

interface StyledInputProps {
  error?: boolean
  type?: string
  disabled?: boolean
}
const StyledInput = styled.input<StyledInputProps>(
  ({ error, theme, type, disabled }) => ({
    width: '100%',
    height: theme.space(11),
    marginTop: theme.space(1),
    padding: `0 ${theme.space(3)}`,
    backgroundColor: disabled ? theme.colors.base_3 : theme.colors.base_0,
    color: theme.colors.base_100,
    border: `1px solid ${error ? theme.colors.critical : theme.colors.base_20}`,
    borderRadius: theme.constants.borderRadius,
    fontSize: '1.5rem',

    '&:not([disabled]):hover': {
      borderColor: error ? theme.colors.critical : theme.colors.primary_2,
    },

    '&:not([disabled]):focus': {
      color: theme.colors.base_100,
      outline: 'none',
      border: `2px solid ${
        error ? theme.colors.critical : theme.colors.primary_2
      }`,
    },
  })
)

const StyledValidationErrorText = styled.label(({ theme }) => ({
  marginTop: theme.space(2),
  color: theme.colors.critical,
  fontSize: '1.3rem',
}))

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string | JSX.Element
  helpLabel?: string | JSX.Element
  helpTooltip?: string | JSX.Element
  type?: string
  errors?: Record<string, unknown>
  focusOnLoad?: boolean
  verticallySpaced?: boolean
  verticallySpace?: number
  containerStyle?: React.CSSProperties
  name: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helpLabel,
      helpTooltip,
      type,
      focusOnLoad,
      errors,
      verticallySpaced = true,
      verticallySpace = 8,
      containerStyle,
      ...props
    },
    ref
  ) => {
    return (
      <StyledInputContainer
        verticallySpaced={verticallySpaced}
        verticallySpace={verticallySpace}
        style={containerStyle}
      >
        {label && <StyledLabel>{label}</StyledLabel>}
        {helpLabel && (
          <StyledHelpLabel>
            {helpLabel}
            {helpTooltip && <HelperTooltip>{helpTooltip}</HelperTooltip>}
          </StyledHelpLabel>
        )}
        <StyledInput
          ref={ref}
          type={type || 'text'}
          autoFocus={focusOnLoad}
          error={!!(errors && errors[props.name])}
          {...props}
        />
        {errors && (
          <ErrorMessage
            errors={errors}
            name={props.name}
            render={({ message }) => (
              <StyledValidationErrorText>{message}</StyledValidationErrorText>
            )}
          />
        )}
      </StyledInputContainer>
    )
  }
)

export default Input
